import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
// biome-ignore lint/style/useImportType: Classic JSX runtime requires React.
import React from "react";
import { useTranslation } from "react-i18next";

import { TITLE_BAR_HEIGHT, hasCustomTitleBar } from "../utils/desktopShell";
import ModalDragStrip from "./ModalDragStrip";

type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** While busy, the modal refuses every dismissal path (escape, click-away, close button). */
  busy?: boolean;
  size?: "md" | "lg" | "wide";
  children: React.ReactNode;
};

const maxWidthClasses = { md: "max-w-md", lg: "max-w-lg", wide: "max-w-6xl" };

// Viewport margin around the centred card. In the desktop shell it also keeps
// the card out of the title-bar band: Electron resolves drag regions
// geometrically, so a card overlapping the strip would have its close button
// drag the window instead of closing the dialog.
const viewportMargin = hasCustomTitleBar ? `calc(${TITLE_BAR_HEIGHT} + 8px)` : "1rem";

/**
 * Radix dialog shell shared by the app's modals: the backdrop, the window drag
 * strip layer, the centred card and the close button. See `ModalDragStrip` for
 * why the strip lives in its own `pointer-events-none` layer between the two.
 */
const Modal = ({ open, onOpenChange, busy = false, size = "md", children }: ModalProps) => {
  const { t } = useTranslation();
  return (
    <Dialog.Root
      open={open}
      onOpenChange={nextOpen => {
        if (!busy) {
          onOpenChange(nextOpen);
        }
      }}
    >
      <>
        <Dialog.Overlay className="fixed inset-0 z-[110] bg-black/55" />
        {open && (
          <div className="pointer-events-none fixed inset-0 z-[111]">
            <ModalDragStrip />
          </div>
        )}
        <Dialog.Content
          onEscapeKeyDown={event => busy && event.preventDefault()}
          onPointerDownOutside={event => busy && event.preventDefault()}
          style={{ maxHeight: `calc(100vh - 2 * ${viewportMargin})` }}
          className={`app-no-drag fixed left-1/2 top-1/2 z-[111] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-outline bg-surface p-5 text-text shadow-2xl focus:outline-none ${maxWidthClasses[size]}`}
        >
          <Dialog.Close asChild>
            <button
              type="button"
              aria-label={t("close")}
              disabled={busy}
              className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-lg text-text-mid transition hover:bg-muted-surface hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X aria-hidden="true" size={18} />
            </button>
          </Dialog.Close>
          {children}
        </Dialog.Content>
      </>
    </Dialog.Root>
  );
};

export default Modal;
